/**
 * 頁 15 — 主檔：供應商／貨品（`ui-spec.md` §2 頁 15；**只准管理員**）
 *
 * 呢頁係兩個面板嘅容器：載入資料 → 交落 `SupplierAdminPanel`／`ItemAdminPanel`。
 * ⚠️ 新增之後即刻 `reload()`（唔靠前端自己砌樂觀狀態）→ 畫面同 DB 永遠一致。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hint as hintStyle } from '@coeldery/ui-kit';
import ItemAdminPanel from '../components/ItemAdminPanel';
import SupplierAdminPanel from '../components/SupplierAdminPanel';
import { ApiError, api, type Item, type Supplier } from '../lib/api';

export default function Master() {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [sup, itm] = await Promise.all([api.suppliers(), api.items(filter || undefined)]);
      setSuppliers(sup.suppliers ?? []);
      setItems(itm.items ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <h2 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 800 }}>{t('master.title')}</h2>

      {loading && <p style={hintStyle}>{t('common.loading')}</p>}
      {error && (
        <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 700 }} role="alert">
          {error}{' '}
          <button
            type="button"
            onClick={() => void load()}
            style={{
              border: 'none',
              background: 'none',
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {t('common.refresh')}
          </button>
        </p>
      )}

      {!loading && (
        <div style={{ display: 'grid', gap: '14px' }}>
          <SupplierAdminPanel suppliers={suppliers} onChanged={load} />
          <ItemAdminPanel
            suppliers={suppliers}
            items={items}
            filter={filter}
            onFilterChange={setFilter}
            onChanged={load}
          />
        </div>
      )}
    </>
  );
}
